import type {
    CustomerNewOrderTask as NewOrderTask,
    CustomerOrderTask as OrderTask,
    CustomerTaskOverview as TaskOverview,
    TaskOverviewSortingFields,
} from '@einwert/valuation-api-client'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import { getSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { applyPatch, ReactQueryOptions, sendToSentry, useUploadMultiple, WithReactQueryOptions } from 'ComponentLibrary'

import { Operation } from 'fast-json-patch'
import invariant from 'tiny-invariant'
import { THREAD_KEYS } from 'ClientPortal/src/api/Queries/threads-keys'
import { FILTERED_KEY, TASK_KEYS } from 'ClientPortal/src/api/Queries/tasks-keys'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'

const TASK_CHANGE_LOG_KEYS = {
    all: ['TASK_CHANGE_LOG'],
    single: (taskId: string) => [...TASK_CHANGE_LOG_KEYS.all, taskId] as const,
}

export function useGetTasks({
    orderId,
    status,
    assignee,
    teamId,
    portfolioId,
    searchString,
    sortBy,
}: {
    orderId?: string
    status?: string
    assignee?: number
    assetId?: string
    teamId?: string
    portfolioId?: string
    searchString?: string
    sortBy?: TaskOverviewSortingFields
} = {}) {
    const einwertApiClient = getValuationApiClient().tasks

    const { data: tasksData, isPending } = useQuery({
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
            einwertApiClient.getCustomerTasks(
                { orderId, status, assignee, teamId, portfolioId, searchString, sortBy },
                { customerId: getSelectedCustomer()?.uuid, signal }
            ),
        queryKey: TASK_KEYS.filter({ orderId, status, assignee, teamId, portfolioId, searchString, sortBy }),
    })

    return {
        tasks: tasksData?.tasks || [],
        isTasksPending: isPending,
    }
}

export function useGetTask({ taskId, ...options }: WithReactQueryOptions<{ taskId: string }>) {
    const einwertApiClient = getValuationApiClient().tasks

    const {
        data: taskData,
        isPending,
        isError,
    } = useQuery({
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
            einwertApiClient.getCustomerTask({ taskId }, { customerId: getSelectedCustomer()?.uuid, signal }),
        queryKey: TASK_KEYS.single(taskId),
        ...options,
    })

    return { taskData, isTaskPending: isPending, isErrorGettingTask: isError }
}

export function useGetTaskChangeLog(taskId: string, options?: ReactQueryOptions) {
    const einwertApiClient = getValuationApiClient().tasks

    const {
        data: taskChangeLogData,
        isPending,
        isError,
    } = useQuery({
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
            einwertApiClient.getCustomerTaskChangeLog({ taskId }, { customerId: getSelectedCustomer()?.uuid, signal }),
        queryKey: TASK_CHANGE_LOG_KEYS.single(taskId),
        ...options,
    })

    return { taskChangeLogData, isTaskChangeLogPending: isPending, isErrorGettingChangeLogTask: isError }
}

export function useDeleteTask() {
    const einwertApiClient = getValuationApiClient().tasks
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler()
    const {
        mutate: deleteTask,
        isPending: isDeleteTaskPending,
        isError: isErrorDeletingTask,
    } = useMutation({
        mutationFn: (variables: { taskId: string }) =>
            einwertApiClient.deleteCustomerTask(variables, { customerId: getSelectedCustomer()?.uuid }),
        onError: handleError,
        onSuccess: async (_, variables, _context) => {
            await queryClient.cancelQueries({ queryKey: TASK_KEYS.all })

            queryClient.setQueryData(
                TASK_KEYS.all,
                (tasksData: Awaited<ReturnType<typeof einwertApiClient.getCustomerTasks>> | undefined) => {
                    if (tasksData === undefined) {
                        return undefined
                    }

                    return {
                        ...tasksData,
                        tasks: tasksData.tasks.filter((task) => task.uuid !== variables.taskId),
                    }
                }
            )
        },
    })

    return { deleteTask, isDeleteTaskPending, isErrorDeletingTask }
}

export const TASK_STATUS_INVALIDATION_DELAY_IN_MS = 1000

export function useOptimisticPatchTask({ onSuccess, onError }: WithReactQueryOptions = {}) {
    const einwertApiClient = getValuationApiClient().tasks
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const patchTaskQuery = (variables: { taskId: string; invalidationDelayMilliseconds?: number; operations: Operation[] }) =>
        einwertApiClient.patchCustomerTask(variables, { customerId: getSelectedCustomer()?.uuid })

    const { mutate, isPending } = useMutation({
        mutationFn: patchTaskQuery,
        onSuccess,
        onMutate: async ({ taskId, operations }) => {
            await queryClient.cancelQueries({ queryKey: TASK_KEYS.single(taskId) })
            await queryClient.cancelQueries({ predicate: (query) => query.queryKey.includes(FILTERED_KEY) })

            try {
                const filteredQueries = queryClient.getQueryCache().findAll({ predicate: (query) => query.queryKey.includes(FILTERED_KEY) })

                const singleTaskQuery = queryClient.getQueryCache().find({ queryKey: TASK_KEYS.single(taskId) })

                const snapshots = {
                    filtered: filteredQueries.map((query) => ({
                        queryKey: query.queryKey,
                        data: queryClient.getQueryData(query.queryKey),
                    })),
                    single: singleTaskQuery
                        ? {
                              queryKey: TASK_KEYS.single(taskId),
                              data: queryClient.getQueryData(TASK_KEYS.single(taskId)),
                          }
                        : null,
                }

                filteredQueries.forEach((query) => {
                    queryClient.setQueryData(query.queryKey, (oldData: { tasks: TaskOverview[] }) => {
                        if (oldData === undefined) {
                            return undefined
                        }

                        return {
                            ...oldData,
                            tasks: oldData.tasks.map((task: TaskOverview) => {
                                if (task.uuid === taskId) {
                                    return applyPatch(task, operations).newDocument
                                }
                                return task
                            }),
                        }
                    })
                })

                if (singleTaskQuery) {
                    queryClient.setQueryData(TASK_KEYS.single(taskId), (oldData: OrderTask) =>
                        oldData ? applyPatch(oldData, operations).newDocument : oldData
                    )
                }

                return { snapshots }
            } catch (e) {
                sendToSentry(e)
            }
        },
        onError: (err, { taskId }, context) => {
            context?.snapshots.single && queryClient.setQueryData(TASK_KEYS.single(taskId), context?.snapshots.single.data)
            context?.snapshots.filtered.forEach(({ queryKey, data }) => {
                queryClient.setQueryData(queryKey, data)
            })
            handleError(err)
        },
        onSettled: async (_, _err, { taskId, invalidationDelayMilliseconds }) => {
            if (invalidationDelayMilliseconds !== undefined) {
                await new Promise((resolve) => setTimeout(resolve, invalidationDelayMilliseconds))
            }
            return Promise.all([
                queryClient.invalidateQueries({ queryKey: TASK_CHANGE_LOG_KEYS.single(taskId) }),
                queryClient.invalidateQueries({ queryKey: TASK_KEYS.all }),
                queryClient.invalidateQueries({ queryKey: THREAD_KEYS.all }),
            ])
        },
    })

    return { mutateTask: mutate, isPatchingTask: isPending } // TODO: Rename loading indicator so its not confused with getting a task -> since this is mutating
}

// usePatchTaskDocument uses a dedicated logic to update the documents property of task.
// Using usePatchTask might introduce errors when optimistically updating the filtered part of the snapshot,
// since TaskOverview doesn't have a documents property.
export function usePatchTaskDocument({ onSuccess, onError }: WithReactQueryOptions = {}) {
    const einwertApiClient = getValuationApiClient().tasks
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const patchTaskQuery = (variables: { taskId: string; operations: Operation[] }) =>
        einwertApiClient.patchCustomerTask(variables, { customerId: getSelectedCustomer()?.uuid })

    const { mutate, isPending } = useMutation({
        mutationFn: patchTaskQuery,
        onSuccess,
        onMutate: async ({ taskId, operations }) => {
            await queryClient.cancelQueries({ queryKey: TASK_KEYS.single(taskId) })

            try {
                const snapshots = {
                    queryKey: TASK_KEYS.single(taskId),
                    data: queryClient.getQueryData(TASK_KEYS.single(taskId)),
                }

                queryClient.setQueryData(TASK_KEYS.single(taskId), (oldData: OrderTask) =>
                    oldData ? applyPatch(oldData, operations).newDocument : oldData
                )

                return { snapshots }
            } catch (e) {
                sendToSentry(e)
            }
        },
        onError: (err, { taskId }, context) => {
            queryClient.setQueryData(TASK_KEYS.single(taskId), context?.snapshots.data)
            handleError(err)
        },
        onSettled: async (_, _err, { taskId }) =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: TASK_KEYS.single(taskId) }),
                queryClient.invalidateQueries({ queryKey: TASK_CHANGE_LOG_KEYS.single(taskId) }),
            ]),
    })

    return { mutateTaskDocument: mutate, isPatchingTaskDocument: isPending }
}

export function useCreateTaskForOrder({ userId, onSuccess, onError }: WithReactQueryOptions<{ userId?: number }> = {}) {
    const einwertApiClient = getValuationApiClient().tasks
    const queryClient = useQueryClient()
    const queryKey = TASK_KEYS.all
    const handleError = useGlobalMutationErrorHandler(onError)

    const { isPending, isError, error, mutateAsync } = useMutation({
        mutationFn: (task: NewOrderTask) => einwertApiClient.createCustomerTask({ task }, { customerId: getSelectedCustomer()?.uuid }),
        onSuccess,
        onMutate: async (newTask) => {
            try {
                invariant(userId, 'userId must be defined to optimistically create a new message')
                // Cancel any outgoing refetches
                // (so they don't overwrite our optimistic update)
                await queryClient.cancelQueries({ queryKey })

                // Snapshot the previous value
                const previousSnapshot = queryClient.getQueryData(queryKey)

                // Optimistically update to the new value
                queryClient.setQueryData(queryKey, (previousTasks?: { tasks: OrderTask[] }) => {
                    if (!previousTasks) {
                        return undefined
                    }
                    return { tasks: [...previousTasks.tasks, { ...newTask, images: [], documents: [], created_by: { id: userId } }] }
                })

                return { previousSnapshot }
            } catch (err) {
                sendToSentry(err)
            }
        },
        onError: (err, _, context) => {
            queryClient.setQueryData(queryKey, context?.previousSnapshot)
            handleError(err)
        },
        onSettled: async () => {
            return Promise.all([queryClient.invalidateQueries({ queryKey }), queryClient.invalidateQueries({ queryKey: THREAD_KEYS.all })])
        },
    })

    return {
        isCreatingTask: isPending,
        isCreateTaskError: isError,
        createTaskError: error,
        createTaskAsync: mutateAsync,
    }
}

export function useUploadTaskImages({ onSuccess, onError, taskId }: WithReactQueryOptions<{ taskId: string }>) {
    const einwertApiClient = getValuationApiClient().tasks
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const mutation = useUploadMultiple<File>({
        onSuccess: (data) => {
            onSuccess?.(data)
            void queryClient.invalidateQueries({
                queryKey: TASK_KEYS.single(taskId),
            })
        },
        onError: handleError,
        mutationFn: (image: File) =>
            einwertApiClient.uploadCustomerTaskImage({ taskId, image }, { customerId: getSelectedCustomer()?.uuid }),
        errorMsgFn: (image: File) => image.name,
    })

    return {
        uploadTaskImages: mutation.uploadMultiple,
        isUploadingTaskImages: mutation.isUploading,
        uploadTaskImagesError: mutation.uploadError,
        failedImageUploads: mutation.failedUploads,
        numImagesToUpload: mutation.numToUpload,
    }
}

export function useUploadTaskDocuments({ onSuccess, onError, taskId }: WithReactQueryOptions<{ taskId: string }>) {
    const einwertApiClient = getValuationApiClient().tasks
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const mutation = useUploadMultiple<File>({
        onSuccess: (data) => {
            onSuccess?.(data)
            void queryClient.invalidateQueries({
                queryKey: TASK_KEYS.single(taskId),
            })
        },
        onError: handleError,
        mutationFn: (document: File) =>
            einwertApiClient.uploadCustomerTaskDocument({ taskId, document }, { customerId: getSelectedCustomer()?.uuid }),
        errorMsgFn: (document: File) => document.name,
    })

    return {
        uploadTaskDocuments: mutation.uploadMultiple,
        isUploadingTaskDocuments: mutation.isUploading,
        uploadTaskDocumentsError: mutation.uploadError,
        failedDocumentUploads: mutation.failedUploads,
        numDocumentsToUpload: mutation.numToUpload,
    }
}

export const useFetchNewTaskDocuments = ({ taskId }: WithReactQueryOptions<{ taskId: string }, OrderTask>) => {
    const einwertApiClient = getValuationApiClient().tasks
    const fetchNewTaskDocuments = (variables: { taskId: string }) =>
        einwertApiClient.getCustomerTask(variables, { customerId: getSelectedCustomer()?.uuid })
    const handleError = useGlobalMutationErrorHandler()
    const { mutateAsync, isPending } = useMutation({
        mutationFn: () => fetchNewTaskDocuments({ taskId }),
        onError: handleError,
    })
    return { fetchNewTaskDocuments: mutateAsync, isFetchingNewTaskDocuments: isPending }
}

export function useGetAllTaskFilesZipUrl({ onSuccess, onError }: ReactQueryOptions<string>) {
    const einwertApiClient = getValuationApiClient().tasks
    const handleError = useGlobalMutationErrorHandler(onError)

    const { isPending: isGeneratingFilesZipUrl, mutate: generateFilesZipUrl } = useMutation({
        mutationFn: (variables: { taskId: string }) =>
            einwertApiClient.getDownloadAllCustomerTaskFilesLinkToZip(variables, { customerId: getSelectedCustomer()?.uuid }),
        onSuccess,
        onError: handleError,
    })

    return {
        isGeneratingFilesZipUrl,
        generateFilesZipUrl,
    }
}

export function useGetTaskQueries(taskIds: UUID[]) {
    const einwertApiClient = getValuationApiClient().tasks

    return useQueries({
        queries: taskIds.map((taskId) => ({
            queryKey: TASK_KEYS.single(taskId),
            queryFn: ({ signal }: { signal?: AbortSignal }) =>
                einwertApiClient.getCustomerTask({ taskId }, { customerId: getSelectedCustomer()?.uuid, signal }),
        })),
    })
}
